import { hasValue } from '@repo/utils';
import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';

import { EventEmitter } from '../../shared/EventEmitter';
import type { TCalculateCtcRequest, TCalculateCtcResponse } from '../types/calculate-ctc';
import type { TrajectoryContext, TrajectorySelection, TrajectoryWellboreRef } from '../types/data';
import type { ReadonlyDeep } from '../types/readonly-deep';
import {
  copyTrajectorySelectedReferenceTree,
  EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE,
  type TTrajectorySelectedReferenceTree,
} from '../types/selected-reference-data';
import type { TFactTrajectoryCreateDefaults, TTrajectory } from '../types/trajectory';
import { OperationAbortedError } from '../utils/errors';
import { pickActiveOrFirst } from '../utils/pick-active-or-first';

import type { TrajectoriesApi } from './api/trajectories-api';
import { collectFactTrajectoryRequests, type TWellboreTrajectoryRequest } from './collect-fact-trajectory-requests';
import {
  FactTrajectoryEntity,
  FactTrajectoryPointEntity,
  isLocalNewTrajectoryId,
  type TrajectoryPointMdValidationError,
} from './entities';
import {
  applyFactWriteDtoToEntity,
  applyTrajectoryCreateDefaultsToEntity,
  mapEntityToFactWriteDto,
  mapEntityToTrajectoryDto,
  mapFactTrajectory,
  resolveActiveFlag,
} from './mappers';
import type { TFactTrajectoryPoint, TrajectoryPlanOrFact } from './model';
import { resolveCorrespondingTrajectoryId } from './resolve-corresponding-trajectory-id';

type TInternalWellboreRequest = {
  wellboreId: number;
  trajectoryIds: Set<number>;
};

type TTrajectoryTableEventMap = {
  newPoints: FactTrajectoryPointEntity[];
  updatePoints: FactTrajectoryPointEntity[];
  deletePoints: string[];
};

export type TrajectoryDraftIdProvider = () => number | null;
export type TrajectoryUpsertedListener = (saved: TTrajectory, previousId?: number) => void;
export type TrajectoryDeletedListener = (trajectoryId: number) => void;

let nextTempTrajectoryId = -1;

export class TrajectoryService {
  @observable.ref private _wellId: number | null = null;
  @observable.ref private _planOrFact: TrajectoryPlanOrFact | null = null;
  @observable.ref private _wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null = null;
  @observable.ref private _selectedWellboreId: number | null = null;
  @observable.ref private _selectedTrajectoryId: number | null = null;
  @observable.ref private _selectedReferenceData: TTrajectorySelectedReferenceTree =
    copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);

  /** Full trajectory catalog for the selected wellbore (sidebar / table). */
  @observable.ref private _wellboreTrajectories: FactTrajectoryEntity[] = [];
  @observable private _isWellboreTrajectoriesLoading = false;
  /** Info-only catalog of the parent wellbore (parent trajectory select). */
  @observable.ref private _parentWellboreTrajectories: TTrajectory[] = [];
  @observable private _isParentWellboreTrajectoriesLoading = false;
  @observable private _isSaving = false;

  private readonly api: TrajectoriesApi;
  private abortController: AbortController | null = null;
  private loadGeneration = 0;
  private parentAbortController: AbortController | null = null;
  private parentLoadGeneration = 0;
  @observable.ref private getDraftId: TrajectoryDraftIdProvider | null = null;
  @observable.ref private _draftId: number | null = null;
  private onTrajectoryUpserted: TrajectoryUpsertedListener | null = null;
  private onTrajectoryDeleted: TrajectoryDeletedListener | null = null;
  private lastRequests: TInternalWellboreRequest[] = [];
  /** Loaded reference-well trajectories only; current wellbore lives in _wellboreTrajectories. */
  @observable.ref private _referenceEntitiesByWellbore = new Map<number, FactTrajectoryEntity[]>();
  readonly eventEmitter = new EventEmitter<TTrajectoryTableEventMap>();

  constructor(api: TrajectoriesApi) {
    this.api = api;
    makeObservable(this);
  }

  /** Host provides a live getter (must read observables, not a closed-over primitive). */
  @action.bound
  setDraftIdProvider(getDraftId: TrajectoryDraftIdProvider | null): void {
    this.getDraftId = getDraftId;
  }

  /** Optional host hook (e.g. header trajectory catalog sync). */
  @action.bound
  setTrajectoryUpsertedListener(listener: TrajectoryUpsertedListener | null): void {
    this.onTrajectoryUpserted = listener;
  }

  /** Notify host after create/upsert when the caller finished local post-processing. */
  @action.bound
  notifyTrajectoryUpserted(entity: FactTrajectoryEntity, previousId?: number): void {
    this.onTrajectoryUpserted?.(mapEntityToTrajectoryDto(entity), previousId);
  }

  /** Optional host hook (e.g. header trajectory catalog sync after delete). */
  @action.bound
  setTrajectoryDeletedListener(listener: TrajectoryDeletedListener | null): void {
    this.onTrajectoryDeleted = listener;
  }

  @computed
  get wellId(): number | null {
    return this._wellId;
  }

  @computed
  get wellbores(): ReadonlyDeep<TrajectoryWellboreRef[]> | null {
    return this._wellbores;
  }

  @computed
  get selectedWellboreId(): number | null {
    return this._selectedWellboreId;
  }

  @computed
  get selectedWellbore(): TrajectoryWellboreRef | null {
    if (!hasValue(this._selectedWellboreId)) {
      return null;
    }

    return this._wellbores?.find((item) => item.id === this._selectedWellboreId) ?? null;
  }

  @computed
  get parentWellboreId(): number | null {
    const parentId = this.selectedWellbore?.parentWellboreId;

    return hasValue(parentId) ? parentId : null;
  }

  @computed
  get hasParentWellbore(): boolean {
    return hasValue(this.parentWellboreId);
  }

  @computed
  get parentWellboreTrajectories(): ReadonlyArray<{ id: number; title: string }> {
    return this._parentWellboreTrajectories.map((item) => ({
      id: item.id,
      title: (item.citation?.title ?? '').trim(),
    }));
  }

  @computed
  get isParentWellboreTrajectoriesLoading(): boolean {
    return this._isParentWellboreTrajectoriesLoading;
  }

  /** Active parent trajectory, otherwise the first in the parent catalog. */
  @computed
  get defaultParentTrajectoryId(): number | null {
    return pickActiveOrFirst(this._parentWellboreTrajectories)?.id ?? null;
  }

  @computed
  get selectedTrajectoryId(): number | null {
    return this._selectedTrajectoryId;
  }

  @computed
  get planOrFact(): TrajectoryPlanOrFact | null {
    return this._planOrFact;
  }

  @computed
  get mainFactTrajectory(): FactTrajectoryEntity | null {
    if (this._isWellboreTrajectoriesLoading || !hasValue(this._selectedTrajectoryId)) {
      return null;
    }

    return this._wellboreTrajectories.find((item) => item.id === this._selectedTrajectoryId) ?? null;
  }

  @computed
  get referenceEntitiesByWellbore(): ReadonlyMap<number, FactTrajectoryEntity[]> {
    return this._referenceEntitiesByWellbore;
  }

  @computed
  get referenceTrajectoryRequests(): ReadonlyArray<{ wellboreId: number; trajectoryIds: ReadonlySet<number> }> {
    return this.lastRequests;
  }

  @computed
  get selectedReferenceData(): TTrajectorySelectedReferenceTree {
    return this._selectedReferenceData;
  }

  @computed
  get hasWell(): boolean {
    return hasValue(this._wellId);
  }

  @computed
  get hasWellboreSelection(): boolean {
    return hasValue(this._selectedWellboreId);
  }

  @computed
  get hasTrajectorySelection(): boolean {
    return hasValue(this._selectedTrajectoryId);
  }

  @computed
  get wellboreTrajectories(): FactTrajectoryEntity[] {
    return this._wellboreTrajectories;
  }

  @computed
  get isWellboreTrajectoriesLoading(): boolean {
    return this._isWellboreTrajectoriesLoading;
  }

  @computed
  get isSaving(): boolean {
    return this._isSaving;
  }

  @action.bound
  setContext(context: TrajectoryContext): void {
    const nextWellId = context.wellId;

    if (this._wellId !== nextWellId) {
      this._wellId = nextWellId;
      this._selectedWellboreId = null;
      this._selectedTrajectoryId = null;
      this.invalidateLoadedTrajectoryCache();
    } else {
      this._wellId = nextWellId;
    }

    if ('planOrFact' in context) {
      this._planOrFact = context.planOrFact ?? null;
    }
  }

  @action.bound
  setWellbores(wellbores: ReadonlyDeep<TrajectoryWellboreRef[]> | null): void {
    this._wellbores = wellbores;
  }

  @action.bound
  setSelection(selection: TrajectorySelection): void {
    if ('wellboreId' in selection) {
      const nextWellboreId = selection.wellboreId ?? null;

      if (this._selectedWellboreId !== nextWellboreId) {
        this._selectedWellboreId = nextWellboreId;
        this.invalidateLoadedTrajectoryCache();

        if (!('trajectoryId' in selection)) {
          this._selectedTrajectoryId = null;
        }
      }
    }

    if ('trajectoryId' in selection) {
      this._selectedTrajectoryId = selection.trajectoryId ?? null;
    }
  }

  @action.bound
  onSelectedReferenceDataChange(data: TTrajectorySelectedReferenceTree): void {
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(data);
  }

  getTrajectoryById(trajectoryId: number | null | undefined): FactTrajectoryEntity | null {
    if (!hasValue(trajectoryId)) {
      return null;
    }

    return this._wellboreTrajectories.find((item) => item.id === trajectoryId) ?? null;
  }

  getFieldValue<K extends keyof TFactTrajectoryPoint>(
    entityId: string | null | undefined,
    field: K,
  ): TFactTrajectoryPoint[K] | null {
    if (!entityId) {
      return null;
    }

    return this.mainFactTrajectory?.getPointField(entityId, field) ?? null;
  }

  @action.bound
  addMainPoints(initialDataList: Partial<TFactTrajectoryPoint>[] = [{}]): FactTrajectoryPointEntity[] {
    const trajectory = this.mainFactTrajectory;

    if (!trajectory || trajectory.isStationsSourceLocked || initialDataList.length === 0) {
      return [];
    }

    const points = initialDataList.map((initialData) => new FactTrajectoryPointEntity(initialData));
    trajectory.addPoints(points);
    this.eventEmitter.emit('newPoints', points);

    return points;
  }

  @action.bound
  addMainPoint(): FactTrajectoryPointEntity | null {
    return this.addMainPoints([{}])[0] ?? null;
  }

  @action.bound
  updateMainPointFields(
    updates: { entityId: string; patch: Partial<TFactTrajectoryPoint> }[],
  ): Map<string, TrajectoryPointMdValidationError | null> {
    const errors = new Map<string, TrajectoryPointMdValidationError | null>();
    const trajectory = this.mainFactTrajectory;

    if (!trajectory || updates.length === 0) {
      return errors;
    }

    const updated: FactTrajectoryPointEntity[] = [];

    for (const { entityId, patch } of updates) {
      const point = trajectory.pointsMap.get(entityId);

      if (!point || trajectory.isPointLocked(point)) {
        continue;
      }

      if ('md' in patch) {
        const mdValidationError = trajectory.validatePointMd(entityId, patch.md);
        errors.set(entityId, mdValidationError);

        if (mdValidationError) {
          continue;
        }
      }

      trajectory.updatePoint(entityId, patch);

      const next = trajectory.pointsMap.get(entityId);

      if (next) {
        updated.push(next);
      }
    }

    if (updated.length > 0) {
      this.eventEmitter.emit('updatePoints', updated);
    }

    return errors;
  }

  @action.bound
  updateMainPointField(
    entityId: string,
    patch: Partial<TFactTrajectoryPoint>,
  ): TrajectoryPointMdValidationError | null {
    return this.updateMainPointFields([{ entityId, patch }]).get(entityId) ?? null;
  }

  @computed
  get areMainStationsReadOnly(): boolean {
    return this.mainFactTrajectory?.isStationsSourceLocked ?? false;
  }

  isMainPointLocked(point: FactTrajectoryPointEntity | null | undefined): boolean {
    return this.mainFactTrajectory?.isPointLocked(point) ?? false;
  }

  @action.bound
  deleteMainPoints(entityIds: string[]): string[] {
    if (entityIds.length === 0) {
      return [];
    }

    const removedIds = this.mainFactTrajectory?.removePointsFromUppermost(entityIds) ?? [];

    if (removedIds.length > 0) {
      this.eventEmitter.emit('deletePoints', removedIds);
    }

    return removedIds;
  }

  calculateCtc(request: TCalculateCtcRequest, signal?: AbortSignal): Promise<TCalculateCtcResponse> {
    const wellId = this._wellId;

    if (wellId == null) {
      return Promise.resolve({ rows: [] });
    }

    return this.api.calculateCtc(wellId, request, signal);
  }

  /** PUT empty trajectory with wellboreId only; applies server response as a new catalog entity. */
  @flow.bound
  async *createFactTrajectory(wellboreId: number) {
    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      return null;
    }

    this._isSaving = true;

    try {
      const response: Awaited<ReturnType<TrajectoriesApi['upsertFactTrajectory']>> =
        yield this.api.upsertFactTrajectory(wellId, draftId, { wellboreId });

      const entity = new FactTrajectoryEntity(
        {
          id: response.id ?? -1,
          wellboreId,
          citation: {
            title: response.title ?? '',
            description: response.description ?? '',
          },
        },
        [],
      );

      applyFactWriteDtoToEntity(entity, response);

      this._wellboreTrajectories = [...this._wellboreTrajectories, entity];

      this.setSelection({ trajectoryId: entity.id });

      return entity;
    } finally {
      this._isSaving = false;
    }
  }

  @action.bound
  createLocalTrajectory(): FactTrajectoryEntity | null {
    const wellboreId = this._selectedWellboreId;

    if (!hasValue(wellboreId)) {
      return null;
    }

    const entity = new FactTrajectoryEntity(
      {
        id: nextTempTrajectoryId--,
        wellboreId,
        citation: {
          title: '',
          description: '',
        },
      },
      [],
    );

    this._wellboreTrajectories = [...this._wellboreTrajectories, entity];

    return entity;
  }

  @flow.bound
  async *fetchFactTrajectoryCreateDefaults(wellboreId: number) {
    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      return undefined;
    }

    try {
      const defaults = await this.api.fetchFactTrajectoryCreateDefaults(wellId, draftId, wellboreId);
      yield;

      return defaults;
    } catch (_e) {
      yield;
      throw _e;
    }
  }

  @action.bound
  applyFactTrajectoryCreateDefaults(entity: FactTrajectoryEntity, defaults: TFactTrajectoryCreateDefaults): void {
    applyTrajectoryCreateDefaultsToEntity(entity, defaults);
  }

  @action.bound
  removeWellboreTrajectory(trajectoryId: number): void {
    this._wellboreTrajectories = this._wellboreTrajectories.filter((item) => item.id !== trajectoryId);
  }

  @action.bound
  private reselectAfterTrajectoryRemoved(removedTrajectoryId: number): void {
    if (this._selectedTrajectoryId !== removedTrajectoryId) {
      return;
    }

    const next = this._wellboreTrajectories[0] ?? null;
    this.setSelection({ trajectoryId: next?.id ?? null });
  }

  /** DELETE fact trajectory draft. Local (negative id) items are removed without a request. */
  @flow.bound
  async *deleteFactTrajectory(trajectoryId: number) {
    if (isLocalNewTrajectoryId(trajectoryId)) {
      this.removeWellboreTrajectory(trajectoryId);
      this.reselectAfterTrajectoryRemoved(trajectoryId);
      this.onTrajectoryDeleted?.(trajectoryId);
      return;
    }

    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      throw new Error('wellId or draftId is not presented');
    }

    this._isSaving = true;

    try {
      yield this.api.deleteFactTrajectory(wellId, draftId, trajectoryId);
      this.removeWellboreTrajectory(trajectoryId);
      this.reselectAfterTrajectoryRemoved(trajectoryId);
      this.onTrajectoryDeleted?.(trajectoryId);
    } finally {
      this._isSaving = false;
    }
  }

  /** Maps entity → fact DTO, PUT via API, commits into the catalog entity. Uses host wellId + draftId. */
  @flow.bound
  async *upsertFactTrajectory(entity: FactTrajectoryEntity) {
    const wellId = this._wellId;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;
    const previousId = entity.id;

    if (!hasValue(wellId) || !hasValue(draftId)) {
      throw new Error('wellId or draftId is not presented');
    }

    this._isSaving = true;

    try {
      const body = mapEntityToFactWriteDto(entity);
      const response: Awaited<ReturnType<TrajectoriesApi['upsertFactTrajectory']>> =
        yield this.api.upsertFactTrajectory(wellId, draftId, body);

      const previousPointIds = entity.points.map((point) => point.entityId);

      applyFactWriteDtoToEntity(entity, response);

      const nextPointIds = new Set(entity.points.map((point) => point.entityId));
      const removedPointIds = previousPointIds.filter((id) => !nextPointIds.has(id));

      if (removedPointIds.length > 0) {
        this.eventEmitter.emit('deletePoints', removedPointIds);
      }

      if (entity.points.length > 0) {
        this.eventEmitter.emit('updatePoints', [...entity.points]);
      }

      this.setSelection({ trajectoryId: entity.id });
      this.notifyTrajectoryUpserted(entity, previousId);

      return entity;
    } finally {
      this._isSaving = false;
    }
  }

  /** Force re-fetch of loaded wellbore trajectories (e.g. after host synced trajectory-data). */
  @flow.bound
  async *reloadWellboreTrajectories() {
    yield this.syncLoadedTrajectories();
  }

  @flow.bound
  private async *syncParentWellboreTrajectories() {
    this.parentAbortController?.abort();
    this.parentAbortController = null;

    const generation = ++this.parentLoadGeneration;
    const parentWellboreId = this.parentWellboreId;

    if (!hasValue(parentWellboreId) || this._planOrFact === 'plan') {
      this._parentWellboreTrajectories = [];
      this._isParentWellboreTrajectoriesLoading = false;
      return;
    }

    this._isParentWellboreTrajectoriesLoading = true;

    const abortController = new AbortController();
    this.parentAbortController = abortController;
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;

    try {
      const trajectories: TTrajectory[] = yield this.api.fetchByWellbore(
        parentWellboreId,
        abortController.signal,
        draftId,
        { infoOnly: true },
      );

      if (generation !== this.parentLoadGeneration) {
        return;
      }

      this._parentWellboreTrajectories = trajectories.map((item) => {
        const active = resolveActiveFlag(item);

        return {
          ...item,
          ...(active !== undefined ? { active } : {}),
        };
      });
    } catch (e) {
      if (generation !== this.parentLoadGeneration || e instanceof OperationAbortedError) {
        return;
      }

      this._parentWellboreTrajectories = [];
    } finally {
      if (generation === this.parentLoadGeneration && this.parentAbortController === abortController) {
        this.parentAbortController = null;
        this._isParentWellboreTrajectoriesLoading = false;
      }
    }
  }

  @flow.bound
  private async *syncLoadedTrajectories() {
    this.abortController?.abort();
    this.abortController = null;

    const generation = ++this.loadGeneration;

    if (this._planOrFact === 'plan') {
      this.lastRequests = [];
      this._referenceEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    const collected: TWellboreTrajectoryRequest[] = collectFactTrajectoryRequests({
      planOrFact: this._planOrFact,
      selectedWellboreId: this._selectedWellboreId,
      selectedReferenceData: this._selectedReferenceData,
    });

    const requests: TInternalWellboreRequest[] = collected.map((item) => ({
      wellboreId: item.wellboreId,
      trajectoryIds: new Set(item.trajectoryIds),
    }));

    this.lastRequests = requests;

    if (requests.length === 0) {
      this._referenceEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
      this._selectedTrajectoryId = null;
      this._isWellboreTrajectoriesLoading = false;
      return;
    }

    this._isWellboreTrajectoriesLoading = true;
    const wellboreIds = [...new Set(requests.map((item) => item.wellboreId))];

    const abortController = new AbortController();
    this.abortController = abortController;

    try {
      const results: PromiseSettledResult<{ wellboreId: number; trajectories: TTrajectory[] }>[] =
        yield Promise.allSettled(
          wellboreIds.map(async (wellboreId) => {
            const trajectories = await this.api.fetchByWellbore(
              wellboreId,
              abortController.signal,
              this.resolveDraftId(wellboreId),
            );
            return { wellboreId, trajectories };
          }),
        );

      if (generation !== this.loadGeneration) {
        return;
      }

      const loadedByWellbore = new Map<number, TTrajectory[]>();

      for (const result of results) {
        if (result.status === 'fulfilled') {
          loadedByWellbore.set(result.value.wellboreId, result.value.trajectories);
        }
      }

      this.remapSelectedTrajectoryToDraft(requests, loadedByWellbore);
      this.reconcileSelectedTrajectoryId(loadedByWellbore);
      this.replaceLoadedEntities(loadedByWellbore);
    } catch (e) {
      if (generation !== this.loadGeneration || e instanceof OperationAbortedError) {
        return;
      }

      this._referenceEntitiesByWellbore = new Map();
      this._wellboreTrajectories = [];
    } finally {
      if (generation === this.loadGeneration && this.abortController === abortController) {
        this.abortController = null;
        this._isWellboreTrajectoriesLoading = false;
      }
    }
  }

  @action.bound
  private replaceLoadedEntities(loadedByWellbore: Map<number, TTrajectory[]>): void {
    const referenceNext = new Map<number, FactTrajectoryEntity[]>();
    const selectedWellboreId = this._selectedWellboreId;

    for (const [wellboreId, rawList] of loadedByWellbore) {
      const entities = rawList.map(mapFactTrajectory);

      if (wellboreId === selectedWellboreId) {
        const previousById = new Map(this._wellboreTrajectories.map((entity) => [entity.id, entity]));

        // Keep dirty local drafts (e.g. defaults after create) while refreshing siblings' active flags.
        this._wellboreTrajectories = entities.map((entity) => {
          const previous = previousById.get(entity.id);

          if (previous?.hasChanges) {
            return previous;
          }

          return entity;
        });
        continue;
      }

      referenceNext.set(wellboreId, entities);
    }

    if (!hasValue(selectedWellboreId) || !loadedByWellbore.has(selectedWellboreId)) {
      this._wellboreTrajectories = [];
    }

    this._referenceEntitiesByWellbore = referenceNext;
  }

  /** When a section draft is active, selected id may still be the published trajectory. */
  @action.bound
  private remapSelectedTrajectoryToDraft(
    requests: TInternalWellboreRequest[],
    loadedByWellbore: Map<number, TTrajectory[]>,
  ): void {
    const draftId = this._draftId ?? this.getDraftId?.() ?? null;
    const wellboreId = this._selectedWellboreId;

    if (!hasValue(draftId) || !hasValue(wellboreId)) {
      return;
    }

    const loaded = loadedByWellbore.get(wellboreId) ?? [];
    const mappedId =
      resolveCorrespondingTrajectoryId(this._selectedTrajectoryId, loaded) ?? pickActiveOrFirst(loaded)?.id ?? null;

    if (!hasValue(mappedId)) {
      if (hasValue(this._selectedTrajectoryId)) {
        this._selectedTrajectoryId = null;
      }

      return;
    }

    if (mappedId === this._selectedTrajectoryId) {
      return;
    }

    const selectedRequest = requests.find((item) => item.wellboreId === wellboreId);
    selectedRequest?.trajectoryIds.add(mappedId);
    this._selectedTrajectoryId = mappedId;
  }

  /** Clears or remaps selected id when it is missing from the loaded catalog. */
  @action.bound
  private reconcileSelectedTrajectoryId(loadedByWellbore: Map<number, TTrajectory[]>): void {
    const wellboreId = this._selectedWellboreId;

    if (!hasValue(wellboreId) || !hasValue(this._selectedTrajectoryId)) {
      return;
    }

    const loaded = loadedByWellbore.get(wellboreId) ?? [];
    const remappedId = resolveCorrespondingTrajectoryId(this._selectedTrajectoryId, loaded);

    if (hasValue(remappedId)) {
      if (remappedId !== this._selectedTrajectoryId) {
        this._selectedTrajectoryId = remappedId;
      }

      return;
    }

    const existsInLoaded = loaded.some((item) => item.id === this._selectedTrajectoryId);

    if (!existsInLoaded) {
      this._selectedTrajectoryId = null;
    }
  }

  @action.bound
  private invalidateLoadedTrajectoryCache(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.loadGeneration += 1;
    this.lastRequests = [];
    this._referenceEntitiesByWellbore = new Map();
    this._wellboreTrajectories = [];
    this._parentWellboreTrajectories = [];
  }

  private isCurrentWellWellbore(wellboreId: number): boolean {
    if (this._selectedWellboreId === wellboreId) {
      return true;
    }

    return this._wellbores?.some((item) => item.id === wellboreId) ?? false;
  }

  private resolveDraftId(wellboreId: number): number | null {
    if (!this.isCurrentWellWellbore(wellboreId)) {
      return null;
    }

    return this._draftId ?? this.getDraftId?.() ?? null;
  }

  @action.bound
  private dispose(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.loadGeneration += 1;
    this.parentAbortController?.abort();
    this.parentAbortController = null;
    this.parentLoadGeneration += 1;
    this._wellId = null;
    this._planOrFact = null;
    this._wellbores = null;
    this._selectedWellboreId = null;
    this._selectedTrajectoryId = null;
    this._draftId = null;
    this.getDraftId = null;
    this._selectedReferenceData = copyTrajectorySelectedReferenceTree(EMPTY_TRAJECTORY_SELECTED_REFERENCE_TREE);
    this._wellboreTrajectories = [];
    this._isWellboreTrajectoriesLoading = false;
    this._parentWellboreTrajectories = [];
    this._isParentWellboreTrajectoriesLoading = false;
    this.lastRequests = [];
    this._referenceEntitiesByWellbore = new Map();
  }

  init = (): VoidFunction => {
    const disposeFetchReaction = reaction(
      () => ({
        wellId: this._wellId,
        planOrFact: this._planOrFact,
        wellboreId: this._selectedWellboreId,
        draftId: this.getDraftId?.() ?? null,
        selectedReferenceData: this._selectedReferenceData,
      }),
      ({ draftId }) => {
        this._draftId = draftId;
        void this.syncLoadedTrajectories();
      },
      { fireImmediately: true },
    );

    const disposeParentCatalogReaction = reaction(
      () => ({
        planOrFact: this._planOrFact,
        parentWellboreId: this.parentWellboreId,
        draftId: this.getDraftId?.() ?? null,
      }),
      () => {
        void this.syncParentWellboreTrajectories();
      },
      { fireImmediately: true },
    );

    return () => {
      disposeFetchReaction();
      disposeParentCatalogReaction();
      this.dispose();
    };
  };
}
